/**
 * Variant registry for the analytics-dashboard autotest.
 *
 * No test is live right now. The last test (analytics_dashboard_tos_v2_2026_q3)
 * closed to variant_c2_default_tab_papers, whose lever (defaultTab: 'documents')
 * has been promoted into the DEFAULTS in ../AutotestAnalytics — so it is now the
 * control baseline, and its variant entry has been dropped (it would be an A/A arm
 * against control). The socket and this variant library are kept wired for the next
 * test on this surface: to launch one, add a config/ab_tests/*.yml whose alternatives
 * match keys here and point new_ab_test at it in AnalyticsDashboardController#index.
 *
 * Each entry is ONE variant = ONE lever, a Partial<Surfaces> override of the
 * control DEFAULTS in ../AutotestAnalytics. This is the variant-DATA layer: the
 * dashboard components never reference these values directly — they read slots
 * from context, and this map is what resolveSurfaces() layers on top per arm.
 * To drop a variant, delete its entry (and its YAML alternative). When a test is
 * running, its keys must match the alternatives in its config/ab_tests/*.yml.
 *
 * See autotest-onboarding/autotest/analytics_dashboard/variants.md for the
 * hypothesis + falsifiable prediction behind each lever.
 */
import type { ArmSpec } from '../AutotestAnalytics';

export const ARMS: Partial<Record<string, ArmSpec>> = {
  // control: no entry -> resolves to DEFAULTS (byte-identical to production).

  // --- Cluster A: Overview chart legibility & appeal ---
  // A1 higher-contrast, more-saturated series
  variant_a1_high_contrast_series: {
    chartSeriesColors: {
      documentViews: '#127A1A',
      profileViews: '#0044CC',
      uniqueVisitors: '#111111',
    },
  },
  // A2 darker gridlines (control #E9E9E9)
  variant_a2_darker_gridlines: {
    chartGridColor: '#C7C7C7',
  },
  // A3 align the chart to the LATEST design-system tokens
  // (app/assets/stylesheets/design_system/tokens/_lg.scss): brand blue +
  // expanded categorical colors, spread across cool/cool-green/warm for clear
  // separation.
  variant_a3_ds_aligned_palette: {
    chartSeriesColors: {
      documentViews: '#0645b1', // --brand-colors-blue-600 (primary brand)
      profileViews: '#4b979d', // --brand-colors-expanded-colors-turquoise-600
      uniqueVisitors: '#8e6863', // --brand-colors-expanded-colors-peach-600
    },
  },
  // A4 colorblind-safe palette (Okabe-Ito)
  variant_a4_colorblind_safe_palette: {
    chartSeriesColors: {
      documentViews: '#0072B2',
      profileViews: '#E69F00',
      uniqueVisitors: '#999999',
    },
  },
  // A5 recolor only the "unique visitors" line (control gray #4b4b4b gets lost)
  variant_a5_recolor_visitors_series: {
    chartSeriesColors: {
      uniqueVisitors: '#8966D5',
    },
  },

  // --- Cluster B: KPI / stat-tile emphasis (Impact tab) ---
  // B1 bigger + bolder stat number
  variant_b1_bigger_bolder_kpi: {
    kpiTileTypography: { fontSizeClass: 'u-fs64', weightClass: 'u-fw700' },
  },
  // B2 colored stat number (control is gray-darker)
  variant_b2_colored_kpi: {
    kpiTileTypography: { colorClass: 'u-tcBlue' },
  },
  // B3 stronger number-vs-label hierarchy: keep the number at the CONTROL size
  // (unlike B1's enlargement) but bold + darkest, and shrink + lighten the LABEL
  // (control ~18px medium -> 12px regular). The lever is number:label contrast.
  variant_b3_stronger_kpi_hierarchy: {
    kpiTileTypography: {
      weightClass: 'u-fw700',
      colorClass: 'u-tcGrayDarkest',
      labelClass: 'u-fs12 u-fw400 u-tcGrayDark',
    },
  },

  // --- Cluster C: Landing tab & tab order ---
  // C1 land on Impact (free impact report available to own-dashboard owners)
  variant_c1_default_tab_impact: {
    defaultTab: 'impact',
  },
  // C2 (land on Papers) won analytics_dashboard_tos_v2_2026_q3 and was promoted
  // into DEFAULTS (defaultTab: 'documents'), so its variant entry is intentionally
  // gone — re-adding it would be an A/A arm against control.
  // C3 reorder so high-interest tabs come first (gating unchanged)
  variant_c3_reorder_engagement_first: {
    tabOrder: [
      'impact',
      'readers',
      'profile_visitors',
      'overview',
      'documents',
      'cv',
      'countries',
      'keywords',
      'links',
    ],
  },

  // --- Cluster D: Density & layout ---
  // D1 coordinated compact density
  variant_d1_compact_density: {
    densityMode: 'compact',
  },
  // D2 coordinated spacious density (direction test vs D1)
  variant_d2_spacious_density: {
    densityMode: 'spacious',
  },
  // D3 tighten only the inter-panel gap, dramatically (control 30px -> 8px),
  // isolating the gap lever from D1's coordinated padding+gap change
  variant_d3_tighter_panel_gap: {
    panelSpacingPx: 8,
  },
  // D4 sticky tab nav (easier switching; no friction added)
  variant_d4_sticky_tab_nav: {
    stickyTabNav: true,
  },

  // --- Cluster E: Copy & framing (existing data only; no fabricated numbers) ---
  // E1 warm, motivational owner header (control shows none)
  variant_e1_motivational_header: {
    headerCopy:
      "Here's how your research is reaching readers around the world.",
  },
  // E2 larger tab labels (control tab links are u-fs12; bump to u-fs16). JS-
  // driven via cloneElement, so it wins over the hardcoded u-fs12 (later-defined
  // !important utility). Replaces the former empty-state variant, which targeted
  // a different (no-data) cohort and wasn't apples-to-apples with the rest.
  variant_e2_larger_tab_labels: {
    tabLabelClass: 'u-fs16',
  },
  // E3 nudge to the existing date-range control (copy only, no new control)
  variant_e3_date_range_hint: {
    dateRangeHintCopy: 'Tip: change the range to see more of your history.',
  },
  // E4 clearer / more inviting tab labels (same destinations)
  variant_e4_energized_tab_labels: {
    tabLabels: {
      readers: "Who's reading",
      impact: 'Your impact',
      profile_visitors: 'Who visited',
    },
  },
};
